import { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data.user);
      setOrganization(response.data.organization);
    } catch (error) {
      console.error('Failed to fetch current user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const applyAuth = (data) => {
    const { access_token, user: userData, organization: orgData } = data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('userEmail', userData.email); // Store email for owner check
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    setOrganization(orgData);
  };

  const login = async (email, password, latitude = null, longitude = null) => {
    const payload = { email, password };
    if (latitude != null && longitude != null) {
      payload.latitude = latitude;
      payload.longitude = longitude;
    }
    const response = await axios.post(`${API}/auth/login`, payload);

    // Staff MFA challenge: no token issued yet — caller shows the code screen
    if (response.status === 202 && response.data?.mfa_required) {
      return response.data;
    }

    applyAuth(response.data);
    return response.data;
  };

  const completeMfaLogin = async (challengeId, code) => {
    const response = await axios.post(`${API}/auth/login/mfa`, { challengeId, code });
    applyAuth(response.data);
    return response.data;
  };

  const resendMfaCode = async (challengeId) => {
    const { data } = await axios.post(`${API}/auth/login/mfa/resend`, { challengeId });
    return data;
  };

  const register = async (email, password, name, organizationName, phone) => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      name,
      organizationName,
      phone
    });
    
    // Check if account requires verification (no token returned)
    if (response.data.requires_verification) {
      // Don't log in the user - they need to verify email first
      return response.data;
    }
    
    // Only for owner account or verified accounts - token is returned
    const { access_token, user: userData, organization: orgData } = response.data;
    
    if (access_token) {
      localStorage.setItem('token', access_token);
      localStorage.setItem('userEmail', userData.email);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      setToken(access_token);
      setUser(userData);
      setOrganization(orgData);
    }
    
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userEmail');
    sessionStorage.removeItem('sidebarExpandedSections');
    sessionStorage.removeItem('welcomeShown');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setOrganization(null);
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data.user);
      setOrganization(response.data.organization);
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, organization, token, loading, login, completeMfaLogin, resendMfaCode, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
